import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { inventarioService, categoriasService, familiasService, movimientosStockService } from '../../services/tiendaService';

const C = {
  green: '#34D399',
  lime: '#D6EF4E',
  blue: '#60A5FA',
  purple: '#A78BFA',
  orange: '#FB923C',
  red: '#F87171',
  yellow: '#FBBF24',
  white: '#FFFFFF',
};

const fmt = (n, d = 2) => typeof n === 'number' && Number.isFinite(n)
  ? n.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d })
  : '—';
const eur = n => typeof n === 'number' && Number.isFinite(n) ? fmt(n) + ' €' : '—';
const pct = n => typeof n === 'number' && Number.isFinite(n) ? fmt(n * 100, 1) + '%' : '—';

const TABS = [
  { id: 'resumen', label: 'Operativo', icon: 'LayoutDashboard' },
  { id: 'rotacion', label: 'Rotación', icon: 'RefreshCw' },
  { id: 'rentabilidad', label: 'Rentabilidad', icon: 'Percent' },
  { id: 'valoracion', label: 'Valoración', icon: 'DollarSign' },
  { id: 'stock', label: 'Stock', icon: 'Package' },
  { id: 'compras', label: 'Compras', icon: 'ShoppingCart' },
  { id: 'alertas', label: 'Alertas', icon: 'AlertTriangle' },
];

const Card = ({ children, className = '', style = {} }) => (
  <div className={'rounded-2xl ' + className} style={{
    background: 'linear-gradient(145deg, rgba(255,255,255,0.045), rgba(255,255,255,0.018))',
    border: '1px solid rgba(255,255,255,0.075)',
    boxShadow: '0 12px 35px rgba(0,0,0,0.12)',
    ...style
  }}>{children}</div>
);

const SectionTitle = ({ title, subtitle, icon, color = C.purple, right }) => (
  <div className="flex items-start justify-between gap-3 mb-5">
    <div className="flex items-center gap-3 min-w-0">
      {icon && (
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: color + '13', border: '1px solid ' + color + '28' }}>
          <Icon name={icon} size={16} style={{ color }} />
        </div>
      )}
      <div className="min-w-0">
        <h3 className="font-oranienbaum font-bold text-white text-lg truncate">{title}</h3>
        {subtitle && <p className="font-oranienbaum text-white/38 text-xs mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {right}
  </div>
);

const Donut = ({ value = 0, color, label, sublabel, size = 122 }) => {
  const safe = Math.max(0, Math.min(100, Number(value) || 0));
  const r = 44;
  const circumference = 2 * Math.PI * r;
  const dash = circumference * safe / 100;
  return (
    <div className="flex flex-col items-center justify-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox="0 0 100 100" className="-rotate-90">
          <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="9" />
          <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="9"
            strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference - dash}
            style={{ transition: 'stroke-dashoffset .6s ease' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-oranienbaum font-bold text-white text-xl">{fmt(safe, 0)}%</span>
          <span className="font-oranienbaum text-white/35 text-[9px] uppercase tracking-wider">{sublabel}</span>
        </div>
      </div>
      <span className="font-oranienbaum text-white/75 text-sm mt-1">{label}</span>
    </div>
  );
};

const MiniBar = ({ label, value, max, color, suffix = '' }) => {
  const width = max > 0 ? Math.max(2, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-oranienbaum text-white/55 text-xs truncate">{label}</span>
        <span className="font-oranienbaum text-white/75 text-xs flex-shrink-0">{fmt(value, 0)}{suffix}</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div className="h-full rounded-full" style={{ width: width + '%', background: color }} />
      </div>
    </div>
  );
};

const KPI = ({ label, value, detail, icon, color }) => (
  <Card className="p-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-oranienbaum text-white/42 text-xs">{label}</p>
        <p className="font-oranienbaum font-bold text-white text-xl md:text-2xl mt-1 truncate">{value}</p>
        {detail && <p className="font-oranienbaum text-white/30 text-[10px] mt-1">{detail}</p>}
      </div>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: color + '14', border: '1px solid ' + color + '28' }}>
        <Icon name={icon} size={16} style={{ color }} />
      </div>
    </div>
  </Card>
);

const RankingTable = ({ title, items, columns, color = C.green, compact = false }) => (
  <Card className="overflow-hidden">
    <div className="px-4 py-3.5 flex items-center justify-between"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.025)' }}>
      <h3 className="font-oranienbaum font-bold text-white text-sm">{title}</h3>
      <span className="font-oranienbaum text-[10px] uppercase tracking-wider" style={{ color }}>{items?.length || 0}</span>
    </div>
    <div className="overflow-hidden">
      <table className="w-full table-fixed">
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <th className="w-9 px-3 py-2 text-left font-oranienbaum text-white/25 text-[9px] uppercase">#</th>
            {columns?.map(col => (
              <th key={col.key} className="px-2 py-2 text-left font-oranienbaum text-white/25 text-[9px] uppercase"
                style={{ width: col.width || 'auto' }}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(items || []).slice(0, compact ? 7 : 10).map((item, i) => (
            <tr key={item?.producto_id || item?.id || i}
              style={{ borderBottom: '1px solid rgba(255,255,255,0.035)' }}>
              <td className="px-3 py-2.5 align-top">
                <span className="font-oranienbaum text-xs font-bold" style={{ color: i < 3 ? color : 'rgba(255,255,255,.28)' }}>#{i + 1}</span>
              </td>
              {columns?.map(col => (
                <td key={col.key} className="px-2 py-2.5 align-top">
                  <div className="font-oranienbaum text-xs leading-4 break-words"
                    style={{ color: col.highlight ? color : 'rgba(255,255,255,.68)' }}>
                    {col.render ? col.render(item) : (item?.[col.key] ?? '—')}
                  </div>
                </td>
              ))}
            </tr>
          ))}
          {(!items || items.length === 0) && (
            <tr><td colSpan={(columns?.length || 0) + 1} className="px-4 py-8 text-center">
              <span className="font-oranienbaum text-white/25 text-xs">Sin datos disponibles</span>
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  </Card>
);

const TiendaAnalitica = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('resumen');
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [inv, cats, fams, movs] = await Promise.all([
        inventarioService?.getAll({}),
        categoriasService?.getAll(),
        familiasService?.getAll(),
        movimientosStockService?.getAll({
          dateFrom: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        }),
      ]);
      setItems(inv || []);
      setCategorias(cats || []);
      setFamilias(fams || []);
      setMovimientos(movs || []);
    } catch (e) {
      setError(e?.message || 'Error al cargar analítica');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const nowTs = Date.now();
  const daysSince = date => {
    if (!date) return null;
    const t = new Date(date).getTime();
    if (!Number.isFinite(t)) return null;
    return Math.max(0, Math.floor((nowTs - t) / 86400000));
  };

  const movementByProduct = {};
  (movimientos || []).forEach(m => {
    const id = m?.producto_id;
    if (!id) return;
    if (!movementByProduct[id]) movementByProduct[id] = {
      ventas30: 0, ventas90: 0, entradas90: 0, mermas90: 0, ultimaVenta: null
    };
    const b = movementByProduct[id];
    const age = daysSince(m?.created_at);
    const qty = Math.abs(Number(m?.cantidad) || 0);
    if (m?.tipo_movimiento === 'VENTA') {
      if (age != null && age <= 30) b.ventas30 += qty;
      if (age != null && age <= 90) b.ventas90 += qty;
      if (!b.ultimaVenta || new Date(m.created_at) > new Date(b.ultimaVenta)) b.ultimaVenta = m.created_at;
    }
    if (m?.tipo_movimiento === 'ENTRADA') b.entradas90 += qty;
    if (m?.tipo_movimiento === 'MERMA') b.mermas90 += qty;
  });

  const enrichedItems = (items || []).map(item => {
    const iva = Number(item?.producto?.tipo_iva ?? 10);
    const costeSinIva = Number(item?.coste_medio ?? 0);
    const costeConIva = costeSinIva * (1 + iva / 100);
    const pvp = item?.producto?.pvp_venta ?? item?.producto?.pvp_sin_iva ?? null;
    const pvpFinal = pvp != null ? Number(pvp) : null;
    const pvpNeto = pvpFinal != null ? pvpFinal / (1 + iva / 100) : null;
    const stock = Number(item?.stock_actual ?? 0);
    const valorCoste = stock * costeSinIva;
    const valorCosteConIva = stock * costeConIva;
    const valorPvp = pvpFinal != null ? stock * pvpFinal : null;
    const margenUnidad = pvpNeto != null ? pvpNeto - costeSinIva : null;
    const margenPot = margenUnidad != null ? stock * margenUnidad : null;
    const margenPct = pvpNeto > 0 ? margenUnidad / pvpNeto : null;
    const cat = categorias?.find(c => c?.id === item?.producto?.categoria?.id);
    const familia = familias?.find(f => f?.id === cat?.familia_id);
    const mov = movementByProduct[item?.producto_id] || {};
    const ventas30 = Number(mov?.ventas30 || 0);
    const ventas90 = Number(mov?.ventas90 || 0);
    const ventaDiaria = ventas30 > 0 ? ventas30 / 30 : ventas90 > 0 ? ventas90 / 90 : 0;
    const cobertura = ventaDiaria > 0 ? stock / ventaDiaria : null;
    const diasSinVenta = mov?.ultimaVenta ? daysSince(mov.ultimaVenta) : null;
    const ventasPvp90 = ventas90 * Number(pvpFinal || 0);
    const costeVentas90 = ventas90 * costeSinIva;
    const margenEstimado90 = pvpNeto != null ? Math.max(0, ventas90 * (pvpNeto - costeSinIva)) : 0;
    const stockMin = Number(item?.producto?.stock_minimo || 0);
    const stockMax = Number(item?.producto?.stock_maximo || 0);
    const puntoPedidoBase = Number(item?.producto?.punto_pedido || 0);
    const puntoPedido = puntoPedidoBase > 0 ? puntoPedidoBase : Math.max(stockMin, Math.ceil(ventaDiaria * 7));
    const objetivoStock = stockMax > 0 ? stockMax : Math.max(stockMin, Math.ceil(ventaDiaria * 14));
    const comprar = stock <= puntoPedido ? Math.max(objetivoStock - stock, 0) : 0;
    const capitalParado = stock > 0 && (diasSinVenta == null || diasSinVenta > 30) ? valorCoste : 0;
    return {
      ...item, iva, costeSinIva, costeConIva, pvp: pvpFinal, pvpNeto, stock,
      valorCoste, valorCosteConIva, valorPvp, margenUnidad, margenPot, margenPct,
      cat, familia, ventas30, ventas90, ventaDiaria, cobertura, diasSinVenta,
      ventasPvp90, costeVentas90, margenEstimado90,
      mermas90: Number(mov?.mermas90 || 0), entradas90: Number(mov?.entradas90 || 0),
      ultimaVenta: mov?.ultimaVenta || null, stockMin, stockMax, puntoPedido,
      objetivoStock, comprar, capitalParado
    };
  });

  const sales90Total = enrichedItems.reduce((s, i) => s + (i?.ventasPvp90 || 0), 0);
  let abcAcum = 0;
  const abcMap = {};
  [...enrichedItems].sort((a, b) => (b?.ventasPvp90 || 0) - (a?.ventasPvp90 || 0)).forEach(i => {
    abcAcum += Number(i?.ventasPvp90 || 0);
    const share = sales90Total > 0 ? abcAcum / sales90Total : 1;
    abcMap[i?.producto_id] = share <= 0.80 ? 'A' : share <= 0.95 ? 'B' : 'C';
  });
  enrichedItems.forEach(i => { i.abc = abcMap[i?.producto_id] || 'C'; });

  const totalCoste = enrichedItems.reduce((s, i) => s + (i?.valorCoste || 0), 0);
  const totalCosteConIva = enrichedItems.reduce((s, i) => s + (i?.valorCosteConIva || 0), 0);
  const totalPvp = enrichedItems.reduce((s, i) => s + (i?.valorPvp || 0), 0);
  const margenTotal = enrichedItems.reduce((s, i) => s + (i?.margenPot || 0), 0);
  const margenPctTotal = totalPvp > 0 ? margenTotal / (totalPvp / 1.10) : 0;
  const unidadesStock = enrichedItems.reduce((s, i) => s + Math.max(i?.stock || 0, 0), 0);
  const referenciasConStock = enrichedItems.filter(i => (i?.stock || 0) > 0).length;
  const sinStock = enrichedItems.filter(i => (i?.stock || 0) <= 0);
  const bajoMinimo = enrichedItems.filter(i => (i?.stock || 0) > 0 && i?.stockMin > 0 && i.stock <= i.stockMin);
  const stockParado = enrichedItems.filter(i => (i?.capitalParado || 0) > 0).sort((a, b) => (b?.capitalParado || 0) - (a?.capitalParado || 0));
  const capitalParadoTotal = stockParado.reduce((s, i) => s + (i?.capitalParado || 0), 0);
  const coberturaItems = enrichedItems.filter(i => i?.cobertura != null && i?.stock > 0);
  const coberturaMedia = coberturaItems.length ? coberturaItems.reduce((s, i) => s + i.cobertura, 0) / coberturaItems.length : null;
  const recomendadosCompra = enrichedItems.filter(i => i?.comprar > 0).sort((a, b) => (b?.valorCoste || 0) - (a?.valorCoste || 0));
  const ventasUnidades30 = enrichedItems.reduce((s, i) => s + (i?.ventas30 || 0), 0);
  const ventasUnidades90 = enrichedItems.reduce((s, i) => s + (i?.ventas90 || 0), 0);
  const ventasPvp90 = enrichedItems.reduce((s, i) => s + (i?.ventasPvp90 || 0), 0);
  const margenEstimado90 = enrichedItems.reduce((s, i) => s + (i?.margenEstimado90 || 0), 0);
  const mermas90 = enrichedItems.reduce((s, i) => s + (i?.mermas90 || 0), 0);
  const entradas90 = enrichedItems.reduce((s, i) => s + (i?.entradas90 || 0), 0);
  const abcA = enrichedItems.filter(i => i.abc === 'A');
  const abcB = enrichedItems.filter(i => i.abc === 'B');
  const abcC = enrichedItems.filter(i => i.abc === 'C');

  const capitalSaludable = Math.max(totalCoste - capitalParadoTotal, 0);
  const stockSaludablePct = totalCoste > 0 ? capitalSaludable / totalCoste * 100 : 0;
  const disponibilidadPct = enrichedItems.length ? referenciasConStock / enrichedItems.length * 100 : 0;
  const abcAPct = enrichedItems.length ? abcA.length / enrichedItems.length * 100 : 0;
  const healthScore = Math.max(0, Math.min(100,
    stockSaludablePct * 0.45 +
    disponibilidadPct * 0.30 +
    (bajoMinimo.length === 0 ? 100 : Math.max(0, 100 - bajoMinimo.length / Math.max(1, enrichedItems.length) * 100)) * 0.25
  ));

  const rankingValorCoste = [...enrichedItems].sort((a, b) => (b?.valorCoste || 0) - (a?.valorCoste || 0));
  const rankingValorPvp = [...enrichedItems].filter(i => i?.valorPvp != null).sort((a, b) => (b?.valorPvp || 0) - (a?.valorPvp || 0));
  const rankingMayorStock = [...enrichedItems].sort((a, b) => (b?.stock || 0) - (a?.stock || 0));
  const rankingMayorMargen = [...enrichedItems].filter(i => i?.margenPct != null).sort((a, b) => (b?.margenPct || 0) - (a?.margenPct || 0));
  const rankingMenorMargen = [...enrichedItems].filter(i => i?.margenPct != null).sort((a, b) => (a?.margenPct || 0) - (b?.margenPct || 0));
  const rankingBajoObjetivo = enrichedItems.filter(i => {
    const real = i?.margenPct;
    const obj = i?.producto?.margen_objetivo ?? i?.cat?.margen_objetivo;
    return real != null && obj != null && real < obj;
  }).sort((a, b) => {
    const da = (a?.producto?.margen_objetivo ?? a?.cat?.margen_objetivo ?? 0) - (a?.margenPct || 0);
    const db = (b?.producto?.margen_objetivo ?? b?.cat?.margen_objetivo ?? 0) - (b?.margenPct || 0);
    return db - da;
  });

  const catValores = categorias.map(cat => {
    const arr = enrichedItems.filter(i => i?.cat?.id === cat?.id);
    return {
      ...cat,
      totalCoste: arr.reduce((s, i) => s + (i?.valorCoste || 0), 0),
      totalPvp: arr.reduce((s, i) => s + (i?.valorPvp || 0), 0),
      numProductos: arr.length
    };
  }).filter(c => c.totalCoste > 0).sort((a, b) => b.totalCoste - a.totalCoste);

  const famValores = familias.map(fam => {
    const arr = enrichedItems.filter(i => i?.familia?.id === fam?.id);
    return {
      ...fam,
      totalCoste: arr.reduce((s, i) => s + (i?.valorCoste || 0), 0),
      totalPvp: arr.reduce((s, i) => s + (i?.valorPvp || 0), 0),
      numProductos: arr.length
    };
  }).filter(f => f.totalCoste > 0).sort((a, b) => b.totalCoste - a.totalCoste);

  return (
    <div className="min-h-screen w-full overflow-x-hidden px-3 py-5 md:px-7 md:py-7"
      style={{ background: 'radial-gradient(circle at 15% 0%, rgba(167,139,250,0.08), transparent 28%), linear-gradient(135deg,#080808 0%,#101010 52%,#0B0B0B 100%)' }}>

      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
              style={{ background: C.purple + '16', border: '1px solid ' + C.purple + '35' }}>
              <Icon name="BarChart2" size={21} style={{ color: C.purple }} />
            </div>
            <div>
              <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Analítica de Inventario</h1>
              <p className="font-oranienbaum text-white/38 text-sm">Centro operativo · stock · rotación · compras · rentabilidad</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-3 py-2 rounded-xl font-oranienbaum text-xs text-white/45"
            style={{ background: 'rgba(255,255,255,.035)', border: '1px solid rgba(255,255,255,.07)' }}>
            Ventana analítica <strong className="text-white/75">90 días</strong>
          </div>
          <button onClick={() => navigate('/tienda-inventario')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum"
            style={{ background: C.green + '12', border: '1px solid ' + C.green + '30', color: C.green }}>
            <Icon name="Package" size={15} /> Inventario
          </button>
          <button onClick={loadData} className="p-2.5 rounded-xl"
            style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.10)', color: 'rgba(255,255,255,.65)' }}>
            <Icon name="RefreshCw" size={15} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-5 p-4 rounded-xl flex items-center gap-3"
          style={{ background: 'rgba(248,113,113,.08)', border: '1px solid rgba(248,113,113,.22)' }}>
          <Icon name="AlertTriangle" size={16} style={{ color: C.red }} />
          <span className="font-oranienbaum text-red-300 text-sm">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
        <KPI label="Stock a coste" value={eur(totalCoste)} detail={fmt(unidadesStock, 0) + ' unidades'} icon="Package" color={C.blue} />
        <KPI label="Ventas · 90 días" value={eur(ventasPvp90)} detail={fmt(ventasUnidades90, 0) + ' unidades'} icon="TrendingUp" color={C.green} />
        <KPI label="Margen estimado · 90 d" value={eur(margenEstimado90)} detail="sobre coste actual" icon="Percent" color={C.lime} />
        <KPI label="Capital parado" value={eur(capitalParadoTotal)} detail={stockParado.length + ' referencias'} icon="AlertTriangle" color={C.orange} />
        <KPI label="Cobertura media" value={coberturaMedia != null ? fmt(coberturaMedia, 0) + ' d' : '—'} detail="ritmo de venta actual" icon="RefreshCw" color={C.yellow} />
        <KPI label="Disponibilidad" value={fmt(disponibilidadPct, 1) + '%'} detail={sinStock.length + ' referencias agotadas'} icon="CheckCircle" color={C.purple} />
      </div>

      <div className="flex gap-1 p-1 rounded-xl mb-5 overflow-x-auto max-w-full"
        style={{ background: 'rgba(255,255,255,.035)', border: '1px solid rgba(255,255,255,.07)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-oranienbaum whitespace-nowrap flex-shrink-0"
            style={activeTab === tab.id
              ? { background: C.purple + '20', color: C.purple, border: '1px solid ' + C.purple + '42' }
              : { color: 'rgba(255,255,255,.42)', border: '1px solid transparent' }}>
            <Icon name={tab.icon} size={14} />{tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex flex-col items-center gap-3">
          <div className="w-9 h-9 rounded-full border-2 border-white/10 border-t-[#A78BFA] animate-spin" />
          <span className="font-oranienbaum text-white/35 text-sm">Construyendo analítica operativa...</span>
        </div>
      ) : (
        <>
          {activeTab === 'resumen' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
                <Card className="xl:col-span-8 p-5">
                  <SectionTitle title="Estado operativo del inventario"
                    subtitle="Lectura rápida para decidir qué vender, comprar o revisar"
                    icon="LayoutDashboard" color={C.purple}
                    right={<span className="font-oranienbaum text-xs px-3 py-1.5 rounded-full" style={{ background: C.green + '12', color: C.green }}>Actualizado</span>} />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-center">
                    <div className="flex justify-center">
                      <Donut value={healthScore} color={healthScore >= 75 ? C.green : healthScore >= 50 ? C.yellow : C.red}
                        label="Salud global" sublabel="score" size={156} />
                    </div>
                    <div className="md:col-span-2 grid grid-cols-2 gap-3">
                      <div className="rounded-xl p-4" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.12)' }}>
                        <p className="font-oranienbaum text-white/40 text-xs">Capital saludable</p>
                        <p className="font-oranienbaum text-xl text-white mt-1">{eur(capitalSaludable)}</p>
                        <p className="font-oranienbaum text-xs mt-1" style={{ color: C.green }}>{fmt(stockSaludablePct, 1)}% del stock</p>
                      </div>
                      <div className="rounded-xl p-4" style={{ background: 'rgba(251,146,60,.06)', border: '1px solid rgba(251,146,60,.12)' }}>
                        <p className="font-oranienbaum text-white/40 text-xs">Capital inmovilizado</p>
                        <p className="font-oranienbaum text-xl text-white mt-1">{eur(capitalParadoTotal)}</p>
                        <p className="font-oranienbaum text-xs mt-1" style={{ color: C.orange }}>{stockParado.length} referencias</p>
                      </div>
                      <div className="rounded-xl p-4" style={{ background: 'rgba(248,113,113,.06)', border: '1px solid rgba(248,113,113,.12)' }}>
                        <p className="font-oranienbaum text-white/40 text-xs">Agotados</p>
                        <p className="font-oranienbaum text-xl text-white mt-1">{fmt(sinStock.length, 0)}</p>
                        <p className="font-oranienbaum text-xs mt-1" style={{ color: C.red }}>{fmt(100 - disponibilidadPct, 1)}% de referencias</p>
                      </div>
                      <div className="rounded-xl p-4" style={{ background: 'rgba(251,191,36,.06)', border: '1px solid rgba(251,191,36,.12)' }}>
                        <p className="font-oranienbaum text-white/40 text-xs">Bajo mínimo</p>
                        <p className="font-oranienbaum text-xl text-white mt-1">{fmt(bajoMinimo.length, 0)}</p>
                        <p className="font-oranienbaum text-xs mt-1" style={{ color: C.yellow }}>requieren atención</p>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="xl:col-span-4 p-5">
                  <SectionTitle title="Composición del stock" subtitle="Dónde está concentrado el capital" icon="Package" color={C.blue} />
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    <Donut value={stockSaludablePct} color={C.green} label="Saludable" sublabel="capital" size={100} />
                    <Donut value={disponibilidadPct} color={C.blue} label="Disponible" sublabel="referencias" size={100} />
                    <Donut value={abcAPct} color={C.lime} label="ABC A" sublabel="referencias" size={100} />
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between"><span className="font-oranienbaum text-white/45 text-xs">Clase A</span><strong className="font-oranienbaum text-sm" style={{ color: C.lime }}>{abcA.length}</strong></div>
                    <div className="flex items-center justify-between"><span className="font-oranienbaum text-white/45 text-xs">Clase B</span><strong className="font-oranienbaum text-sm" style={{ color: C.yellow }}>{abcB.length}</strong></div>
                    <div className="flex items-center justify-between"><span className="font-oranienbaum text-white/45 text-xs">Clase C</span><strong className="font-oranienbaum text-sm" style={{ color: C.red }}>{abcC.length}</strong></div>
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
                <Card className="xl:col-span-4 p-5">
                  <SectionTitle title="Balance económico" subtitle="Valor actual del inventario" icon="DollarSign" color={C.green} />
                  <div className="space-y-4">
                    <div className="flex items-end justify-between gap-3">
                      <span className="font-oranienbaum text-white/45 text-sm">Coste sin IVA</span>
                      <strong className="font-oranienbaum text-white text-xl">{eur(totalCoste)}</strong>
                    </div>
                    <MiniBar label="Coste con IVA" value={totalCosteConIva} max={Math.max(totalPvp, totalCosteConIva)} color={C.purple} suffix=" €" />
                    <MiniBar label="Valor potencial de venta" value={totalPvp} max={Math.max(totalPvp, totalCosteConIva)} color={C.lime} suffix=" €" />
                    <div className="pt-3 mt-2" style={{ borderTop: '1px solid rgba(255,255,255,.06)' }}>
                      <div className="flex items-end justify-between">
                        <span className="font-oranienbaum text-white/45 text-sm">Margen potencial neto</span>
                        <strong className="font-oranienbaum text-xl" style={{ color: C.green }}>{eur(margenTotal)}</strong>
                      </div>
                      <p className="font-oranienbaum text-white/25 text-[10px] mt-1">Calculado sobre PVP neto de IVA y coste medio actual.</p>
                    </div>
                  </div>
                </Card>

                <Card className="xl:col-span-4 p-5">
                  <SectionTitle title="Ritmo comercial" subtitle="Lo que realmente se está moviendo" icon="TrendingUp" color={C.green} />
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,.025)' }}>
                      <p className="font-oranienbaum text-white/35 text-xs">30 días</p>
                      <p className="font-oranienbaum text-2xl text-white mt-1">{fmt(ventasUnidades30, 0)}</p>
                      <p className="font-oranienbaum text-[10px]" style={{ color: C.green }}>unidades</p>
                    </div>
                    <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,.025)' }}>
                      <p className="font-oranienbaum text-white/35 text-xs">90 días</p>
                      <p className="font-oranienbaum text-2xl text-white mt-1">{fmt(ventasUnidades90, 0)}</p>
                      <p className="font-oranienbaum text-[10px]" style={{ color: C.blue }}>unidades</p>
                    </div>
                  </div>
                  <MiniBar label="Entradas de stock · 90 d" value={entradas90} max={Math.max(entradas90, ventasUnidades90, 1)} color={C.blue} />
                  <MiniBar label="Ventas · 90 d" value={ventasUnidades90} max={Math.max(entradas90, ventasUnidades90, 1)} color={C.green} />
                  <MiniBar label="Mermas · 90 d" value={mermas90} max={Math.max(entradas90, ventasUnidades90, mermas90, 1)} color={C.red} />
                </Card>

                <Card className="xl:col-span-4 p-5">
                  <SectionTitle title="Prioridades de hoy" subtitle="Orden de actuación recomendado" icon="AlertTriangle" color={C.orange} />
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between gap-3 p-3 rounded-xl" style={{ background: 'rgba(248,113,113,.055)' }}>
                      <div><p className="font-oranienbaum text-white/75 text-sm">Agotados</p><p className="font-oranienbaum text-white/30 text-[10px]">riesgo de pérdida de venta</p></div>
                      <strong className="font-oranienbaum text-lg" style={{ color: C.red }}>{sinStock.length}</strong>
                    </div>
                    <div className="flex items-center justify-between gap-3 p-3 rounded-xl" style={{ background: 'rgba(251,191,36,.055)' }}>
                      <div><p className="font-oranienbaum text-white/75 text-sm">Bajo mínimo</p><p className="font-oranienbaum text-white/30 text-[10px]">revisar reposición</p></div>
                      <strong className="font-oranienbaum text-lg" style={{ color: C.yellow }}>{bajoMinimo.length}</strong>
                    </div>
                    <div className="flex items-center justify-between gap-3 p-3 rounded-xl" style={{ background: 'rgba(251,146,60,.055)' }}>
                      <div><p className="font-oranienbaum text-white/75 text-sm">Capital parado</p><p className="font-oranienbaum text-white/30 text-[10px]">sin venta &gt; 30 días</p></div>
                      <strong className="font-oranienbaum text-lg" style={{ color: C.orange }}>{eur(capitalParadoTotal)}</strong>
                    </div>
                    <div className="flex items-center justify-between gap-3 p-3 rounded-xl" style={{ background: 'rgba(167,139,250,.055)' }}>
                      <div><p className="font-oranienbaum text-white/75 text-sm">Reposiciones</p><p className="font-oranienbaum text-white/30 text-[10px]">según ritmo de venta</p></div>
                      <strong className="font-oranienbaum text-lg" style={{ color: C.purple }}>{recomendadosCompra.length}</strong>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <RankingTable title="Mayor capital inmovilizado" items={stockParado} color={C.orange} compact
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '54%' },
                    { key: 'dias', label: 'Sin venta', render: i => i?.diasSinVenta == null ? '>90 d' : i.diasSinVenta + ' d', width: '20%' },
                    { key: 'capital', label: 'Capital', render: i => eur(i?.capitalParado), highlight: true, width: '26%' }
                  ]} />
                <RankingTable title="Mayor contribución de ventas · 90 días" items={[...enrichedItems].sort((a, b) => (b?.ventasPvp90 || 0) - (a?.ventasPvp90 || 0))} color={C.green} compact
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '54%' },
                    { key: 'uds', label: 'Uds', render: i => fmt(i?.ventas90, 0), width: '18%' },
                    { key: 'ventas', label: 'PVP 90 d', render: i => eur(i?.ventasPvp90), highlight: true, width: '28%' }
                  ]} />
              </div>
            </div>
          )}

          {activeTab === 'rotacion' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                <KPI label="Ventas 30 días" value={fmt(ventasUnidades30, 0) + ' uds'} icon="RefreshCw" color={C.blue} />
                <KPI label="Ventas 90 días" value={fmt(ventasUnidades90, 0) + ' uds'} icon="TrendingUp" color={C.green} />
                <KPI label="Cobertura media" value={coberturaMedia != null ? fmt(coberturaMedia, 0) + ' d' : '—'} icon="Package" color={C.yellow} />
                <KPI label="Sin rotación &gt;30 d" value={fmt(stockParado.length, 0)} icon="AlertTriangle" color={C.orange} />
              </div>
              <RankingTable title="Rotación, cobertura y clasificación ABC" items={[...enrichedItems].filter(i => i.stock > 0).sort((a, b) => (a?.cobertura ?? 999999) - (b?.cobertura ?? 999999))} color={C.blue}
                columns={[
                  { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '40%' },
                  { key: 'stock', label: 'Stock', render: i => fmt(i?.stock, 0), width: '13%' },
                  { key: 'venta', label: 'Uds/día', render: i => fmt(i?.ventaDiaria, 2), width: '15%' },
                  { key: 'cobertura', label: 'Cobertura', render: i => i?.cobertura != null ? fmt(i.cobertura, 0) + ' d' : 'Sin venta', highlight: true, width: '20%' },
                  { key: 'abc', label: 'ABC', render: i => i?.abc || '—', width: '12%' }
                ]} />
              <RankingTable title="Productos con rotación baja o nula" items={stockParado} color={C.orange}
                columns={[
                  { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '48%' },
                  { key: 'stock', label: 'Stock', render: i => fmt(i?.stock, 0), width: '14%' },
                  { key: 'dias', label: 'Última venta', render: i => i?.diasSinVenta == null ? '>90 d' : 'hace ' + i.diasSinVenta + ' d', width: '18%' },
                  { key: 'capital', label: 'Capital', render: i => eur(i?.capitalParado), highlight: true, width: '20%' }
                ]} />
            </div>
          )}

          {activeTab === 'rentabilidad' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                <KPI label="Margen potencial" value={eur(margenTotal)} detail={pct(margenPctTotal)} icon="Percent" color={C.green} />
                <KPI label="Margen estimado · 90 d" value={eur(margenEstimado90)} detail="ventas realizadas" icon="TrendingUp" color={C.blue} />
                <KPI label="Productos bajo objetivo" value={fmt(rankingBajoObjetivo.length, 0)} icon="TrendingDown" color={C.orange} />
                <KPI label="Valor potencial PVP" value={eur(totalPvp)} icon="DollarSign" color={C.lime} />
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <RankingTable title="Mayor margen estimado generado · 90 días" items={[...enrichedItems].sort((a, b) => (b?.margenEstimado90 || 0) - (a?.margenEstimado90 || 0))} color={C.green}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '52%' },
                    { key: 'margen', label: 'Margen', render: i => eur(i?.margenEstimado90), highlight: true, width: '25%' },
                    { key: 'pct', label: 'Margen %', render: i => pct(i?.margenPct), width: '23%' }
                  ]} />
                <RankingTable title="Menor margen real" items={rankingMenorMargen} color={C.red}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '52%' },
                    { key: 'margenReal', label: 'Margen', render: i => pct(i?.margenPct), highlight: true, width: '23%' },
                    { key: 'pvp', label: 'PVP', render: i => eur(i?.pvp), width: '25%' }
                  ]} />
              </div>
              <RankingTable title="Productos por debajo del margen objetivo" items={rankingBajoObjetivo} color={C.orange}
                columns={[
                  { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '45%' },
                  { key: 'obj', label: 'Objetivo', render: i => pct(i?.producto?.margen_objetivo ?? i?.cat?.margen_objetivo), width: '18%' },
                  { key: 'real', label: 'Real', render: i => pct(i?.margenPct), highlight: true, width: '18%' },
                  { key: 'diff', label: 'Desvío', render: i => {
                    const obj = i?.producto?.margen_objetivo ?? i?.cat?.margen_objetivo ?? 0;
                    return <span style={{ color: C.red }}>{pct((i?.margenPct || 0) - obj)}</span>;
                  }, width: '19%' }
                ]} />
            </div>
          )}

          {activeTab === 'valoracion' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                <KPI label="Coste sin IVA" value={eur(totalCoste)} icon="DollarSign" color={C.blue} />
                <KPI label="Coste con IVA" value={eur(totalCosteConIva)} icon="Receipt" color={C.purple} />
                <KPI label="Valor potencial PVP" value={eur(totalPvp)} icon="TrendingUp" color={C.lime} />
                <KPI label="Margen potencial" value={eur(margenTotal)} icon="Percent" color={C.green} />
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <RankingTable title="Mayor valor de stock a coste" items={rankingValorCoste} color={C.blue}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '55%' },
                    { key: 'stock', label: 'Stock', render: i => fmt(i?.stock, 0), width: '15%' },
                    { key: 'coste', label: 'Valor coste', render: i => eur(i?.valorCoste), highlight: true, width: '30%' }
                  ]} />
                <RankingTable title="Mayor valor potencial a PVP" items={rankingValorPvp} color={C.lime}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '50%' },
                    { key: 'pvp', label: 'PVP', render: i => eur(i?.pvp), width: '20%' },
                    { key: 'valor', label: 'Valor PVP', render: i => eur(i?.valorPvp), highlight: true, width: '30%' }
                  ]} />
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <RankingTable title="Categorías con mayor inversión" items={catValores} color={C.purple}
                  columns={[
                    { key: 'nombre', label: 'Categoría', width: '52%' },
                    { key: 'num', label: 'Productos', render: i => fmt(i?.numProductos, 0), width: '20%' },
                    { key: 'coste', label: 'Valor coste', render: i => eur(i?.totalCoste), highlight: true, width: '28%' }
                  ]} />
                <RankingTable title="Familias con mayor valor de stock" items={famValores} color={C.green}
                  columns={[
                    { key: 'nombre', label: 'Familia', width: '52%' },
                    { key: 'num', label: 'Productos', render: i => fmt(i?.numProductos, 0), width: '20%' },
                    { key: 'coste', label: 'Valor coste', render: i => eur(i?.totalCoste), highlight: true, width: '28%' }
                  ]} />
              </div>
            </div>
          )}

          {activeTab === 'stock' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                <KPI label="Unidades en stock" value={fmt(unidadesStock, 0)} icon="Package" color={C.blue} />
                <KPI label="Referencias con stock" value={fmt(referenciasConStock, 0)} icon="CheckCircle" color={C.green} />
                <KPI label="Sin stock" value={fmt(sinStock.length, 0)} icon="AlertTriangle" color={C.red} />
                <KPI label="Bajo mínimo" value={fmt(bajoMinimo.length, 0)} icon="AlertTriangle" color={C.yellow} />
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <RankingTable title="Mayor stock por unidades" items={rankingMayorStock} color={C.green}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '55%' },
                    { key: 'stock', label: 'Stock', render: i => fmt(i?.stock, 0), highlight: true, width: '20%' },
                    { key: 'valor', label: 'Valor coste', render: i => eur(i?.valorCoste), width: '25%' }
                  ]} />
                <RankingTable title="Productos bajo mínimo" items={bajoMinimo} color={C.yellow}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '52%' },
                    { key: 'actual', label: 'Actual', render: i => fmt(i?.stock, 0), highlight: true, width: '20%' },
                    { key: 'min', label: 'Mínimo', render: i => fmt(i?.stockMin, 0), width: '28%' }
                  ]} />
              </div>
              <RankingTable title={'Productos sin stock (' + sinStock.length + ')'} items={sinStock} color={C.red}
                columns={[
                  { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '55%' },
                  { key: 'cat', label: 'Categoría', render: i => i?.cat?.nombre || '—', width: '25%' },
                  { key: 'pvp', label: 'PVP', render: i => eur(i?.pvp), width: '20%' }
                ]} />
            </div>
          )}

          {activeTab === 'compras' && (
            <div className="space-y-5">
              <Card className="p-5" style={{ background: 'linear-gradient(135deg, rgba(251,146,60,.09), rgba(255,255,255,.02))', borderColor: 'rgba(251,146,60,.20)' }}>
                <SectionTitle title="Centro de reposición" subtitle="Decisiones de compra calculadas con stock, mínimos, punto de pedido y ritmo de venta." icon="ShoppingCart" color={C.orange} />
                <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                  <KPI label="Referencias a reponer" value={fmt(recomendadosCompra.length, 0)} icon="ShoppingCart" color={C.orange} />
                  <KPI label="Unidades recomendadas" value={fmt(recomendadosCompra.reduce((s, i) => s + (i?.comprar || 0), 0), 0)} icon="Package" color={C.yellow} />
                  <KPI label="Capital actual en esas refs." value={eur(recomendadosCompra.reduce((s, i) => s + (i?.valorCoste || 0), 0))} icon="DollarSign" color={C.blue} />
                  <KPI label="Cobertura media" value={coberturaMedia != null ? fmt(coberturaMedia, 0) + ' d' : '—'} icon="RefreshCw" color={C.green} />
                </div>
              </Card>
              <RankingTable title={'Reposición recomendada (' + recomendadosCompra.length + ')'} items={recomendadosCompra} color={C.orange}
                columns={[
                  { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—', width: '39%' },
                  { key: 'actual', label: 'Actual', render: i => fmt(i?.stock, 0), width: '13%' },
                  { key: 'punto', label: 'Punto pedido', render: i => fmt(i?.puntoPedido, 0), width: '16%' },
                  { key: 'comprar', label: 'Comprar', render: i => fmt(i?.comprar, 0), highlight: true, width: '15%' },
                  { key: 'cobertura', label: 'Cobertura', render: i => i?.cobertura != null ? fmt(i.cobertura, 0) + ' d' : '—', width: '17%' }
                ]} />
            </div>
          )}

          {activeTab === 'alertas' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                <KPI label="Alertas de stock" value={fmt(sinStock.length + bajoMinimo.length, 0)} detail="agotados + bajo mínimo" icon="AlertTriangle" color={C.red} />
                <KPI label="Capital parado" value={eur(capitalParadoTotal)} detail="sin venta > 30 días" icon="DollarSign" color={C.orange} />
                <KPI label="Margen bajo objetivo" value={fmt(rankingBajoObjetivo.length, 0)} icon="TrendingDown" color={C.yellow} />
                <KPI label="Reposición pendiente" value={fmt(recomendadosCompra.length, 0)} icon="ShoppingCart" color={C.blue} />
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <Card className="p-5" style={{ background: 'rgba(248,113,113,.045)', borderColor: 'rgba(248,113,113,.15)' }}>
                  <SectionTitle title="Agotados" subtitle="Prioridad comercial" icon="XCircle" color={C.red} />
                  <div className="space-y-2">{sinStock.slice(0, 12).map((i, n) => (
                    <div key={n} className="flex items-center justify-between gap-2 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                      <span className="font-oranienbaum text-white/70 text-xs break-words">{i?.producto?.nombre}</span>
                      <span className="font-oranienbaum text-[10px] flex-shrink-0" style={{ color: C.red }}>AGOTADO</span>
                    </div>
                  ))}</div>
                </Card>
                <Card className="p-5" style={{ background: 'rgba(251,191,36,.045)', borderColor: 'rgba(251,191,36,.15)' }}>
                  <SectionTitle title="Bajo mínimo" subtitle="Revisar compra" icon="AlertTriangle" color={C.yellow} />
                  <div className="space-y-2">{bajoMinimo.slice(0, 12).map((i, n) => (
                    <div key={n} className="flex items-center justify-between gap-2 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                      <span className="font-oranienbaum text-white/70 text-xs break-words">{i?.producto?.nombre}</span>
                      <span className="font-oranienbaum text-[10px] flex-shrink-0" style={{ color: C.yellow }}>{fmt(i?.stock, 0)} / {fmt(i?.stockMin, 0)}</span>
                    </div>
                  ))}</div>
                </Card>
                <Card className="p-5" style={{ background: 'rgba(251,146,60,.045)', borderColor: 'rgba(251,146,60,.15)' }}>
                  <SectionTitle title="Margen bajo objetivo" subtitle="Revisar precio o coste" icon="TrendingDown" color={C.orange} />
                  <div className="space-y-2">{rankingBajoObjetivo.slice(0, 12).map((i, n) => (
                    <div key={n} className="flex items-center justify-between gap-2 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                      <span className="font-oranienbaum text-white/70 text-xs break-words">{i?.producto?.nombre}</span>
                      <span className="font-oranienbaum text-[10px] flex-shrink-0" style={{ color: C.orange }}>{pct(i?.margenPct)}</span>
                    </div>
                  ))}</div>
                </Card>
              </div>
            </div>
          )}

          <div className="mt-5 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,.018)', border: '1px solid rgba(255,255,255,.055)' }}>
            <div className="flex items-start gap-2">
              <Icon name="Info" size={14} className="text-white/25 flex-shrink-0 mt-0.5" />
              <p className="font-oranienbaum text-white/25 text-[10px] leading-4">
                La analítica usa inventario actual y movimientos registrados en los últimos 90 días. La cobertura usa el ritmo de venta observado; el capital parado considera referencias con stock y más de 30 días sin venta o sin venta registrada. El margen potencial usa PVP neto de IVA frente al coste medio sin IVA cuando ambos datos están disponibles.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TiendaAnalitica;
